import { Directive, computed, contentChildren, effect, inject, input, signal } from "@angular/core";
import { DevPanelFilter } from "./dev-panel-filter";
import { DevRowDirective } from "./dev-row.directive";
import type { DevSectionId } from "./dev-panel-shell";

/** Sunk rows sharing one reason, rendered as a single collapsed footer line. */
export interface DevSunkGroup {
  readonly reason: string;
  readonly tier: "inert" | "blocked";
  readonly rows: readonly DevRowDirective[];
}

/**
 * Marks a template block as a dev panel section. A section stays visible
 * while it has at least one row matching the filter query, or when it has no
 * rows at all (e.g. a section that is purely descriptive should not vanish
 * while typing).
 *
 * Rows that cannot work in the current context sink into a per-reason footer
 * at the bottom of the section. The footer auto-expands whenever the filter
 * matches one of its rows, so a sunk control is never unreachable by search.
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: "[devSection]",
  standalone: true,
  exportAs: "devSection",
  host: {
    "[style.display]": "hasMatchingRows() ? null : 'none'",
  },
})
export class DevSectionDirective {
  private readonly filter = inject(DevPanelFilter);
  private readonly manuallyExpanded = signal(false);
  private readonly filterIsActive = computed(() => this.filter.normalizedQuery() !== "");

  readonly id = input<DevSectionId | undefined>(undefined, { alias: "devSectionId" });

  readonly rows = contentChildren(DevRowDirective, { descendants: true });

  /** Rows rendered in their normal position. */
  readonly unsunkRows = computed(() => this.rows().filter((row) => !row.sunk()));

  readonly sunkRows = computed(() => this.rows().filter((row) => row.sunk()));

  readonly sunkGroups = computed<readonly DevSunkGroup[]>(() => {
    const groups = new Map<string, DevSunkGroup>();

    for (const row of this.sunkRows()) {
      const availability = row.availability();
      // sunkRows() already filters on row.sunk(), which enforces the predicate
      // (tier !== "available" && !pinned()), so we only need to extract the
      // reason and tier here without duplicating that logic.
      if (availability.tier === "available") continue;
      const { tier, reason } = availability;

      const existing = groups.get(reason);
      groups.set(reason, {
        reason,
        tier,
        rows: existing ? [...existing.rows, row] : [row],
      });
    }

    return Array.from(groups.values());
  });

  /**
   * True when the filter is matching a row that would otherwise be hidden in
   * the footer. Without this, typing "token" would find nothing while Copy
   * token sits sunk — the exact failure the sink design must avoid.
   *
   * Guarded on an active query because `matches()` returns true for every row
   * when the query is empty, which would expand every footer at rest.
   */
  readonly filterMatchesSunkRow = computed(() => this.filterIsActive() && this.sunkRows().some((row) => row.matches()));

  readonly footerExpanded = computed(() => this.manuallyExpanded() || this.filterMatchesSunkRow());

  readonly hasMatchingRows = computed(() => {
    const rows = this.rows();
    return rows.length === 0 || rows.some((row) => row.matches());
  });

  /**
   * Rows the filter leaves on screen in this section. Ignores whether the
   * section is folded — that is a viewing preference, not a filter result — so
   * the per-section counts reconcile with the panel's "N / M" readout.
   *
   * Repeats the predicate behind `DevRowDirective.reachable` instead of calling
   * it, deliberately. `reachable` reads the row's `footerExpanded`, which this
   * section *pushes* down through an effect, so it is one flush stale: on the
   * flush where a query first matches a sunk row, every row still reports
   * `footerExpanded === false` and this count would read 0 for a section that
   * is about to un-sink a match. A transiently-zero count is harmless for a
   * readout but not for the rail, whose auto-jump would fire on it and move the
   * user off the section that actually holds their match. Reading the section's
   * own `footerExpanded()` keeps the count correct within the flush.
   */
  readonly matchingRowCount = computed(() => {
    const footerExpanded = this.footerExpanded();
    return this.rows().filter((row) => row.matches() && (!row.sunk() || footerExpanded)).length;
  });

  constructor() {
    // Push the footer expanded state down to sunk rows so they can hide themselves
    // when the footer is collapsed. A pull approach (rows injecting their parent
    // section) would be cleaner but creates a circular import.
    effect(() => {
      const footerExpanded = this.footerExpanded();
      for (const row of this.rows()) {
        row.setFooterExpanded(footerExpanded);
      }
    });
  }

  toggleFooter(): void {
    this.manuallyExpanded.update((expanded) => !expanded);
  }
}
